import React, { useState, useMemo } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { FileText, ExternalLink, ChevronRight, ChevronDown, Plus, Minus } from 'lucide-react';

interface PRDiffViewerProps {
  diff: string;
  prUrl: string;
  className?: string;
}

interface FileChange {
  oldPath: string;
  newPath: string;
  type: 'add' | 'delete' | 'modify' | 'rename';
  content: string;
}

const PRDiffViewer: React.FC<PRDiffViewerProps> = ({
  diff,
  prUrl,
  className = ''
}) => {
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<'unified' | 'split'>('unified');

  // Parse the diff (simplified approach)
  const files = useMemo(() => {
    try {
      if (!diff) return [];
      
      // Simple diff parsing - split by file headers
      const fileBlocks = diff.split('diff --git').filter(block => block.trim());
      
      return fileBlocks.map((block, index) => {
        const lines = block.split('\n');
        const firstLine = lines[0] || '';
        
        // Extract file paths
        const pathMatch = firstLine.match(/a\/(.+?) b\/(.+?)$/);
        const oldPath = pathMatch ? pathMatch[1] : `file-${index}`;
        const newPath = pathMatch ? pathMatch[2] : `file-${index}`;
        
        // Determine file type based on content
        let type: 'add' | 'delete' | 'modify' | 'rename' = 'modify';
        if (block.includes('new file mode')) type = 'add';
        else if (block.includes('deleted file mode')) type = 'delete';
        else if (oldPath !== newPath) type = 'rename';
        
        return {
          oldPath,
          newPath,
          type,
          content: block
        };
      });
    } catch (error) {
      console.error('Failed to parse diff:', error);
      return [];
    }
  }, [diff]);

  const toggleFileExpansion = (filePath: string) => {
    const newExpanded = new Set(expandedFiles);
    if (newExpanded.has(filePath)) {
      newExpanded.delete(filePath);
    } else {
      newExpanded.add(filePath);
    }
    setExpandedFiles(newExpanded);
  };

  const getFileIcon = (type: FileChange['type']) => {
    switch (type) {
      case 'add':
        return <Plus className="w-4 h-4 text-green-600" />;
      case 'delete':
        return <Minus className="w-4 h-4 text-red-600" />;
      case 'modify':
        return <FileText className="w-4 h-4 text-blue-600" />;
      case 'rename':
        return <ChevronRight className="w-4 h-4 text-yellow-600" />;
      default:
        return <FileText className="w-4 h-4 text-gray-600" />;
    }
  };

  const getFileTypeColor = (type: FileChange['type']) => {
    switch (type) {
      case 'add':
        return 'border-l-green-500 bg-green-50';
      case 'delete':
        return 'border-l-red-500 bg-red-50';
      case 'modify':
        return 'border-l-blue-500 bg-blue-50';
      case 'rename':
        return 'border-l-yellow-500 bg-yellow-50';
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  const getLanguageFromPath = (path: string) => {
    const extension = path.split('.').pop()?.toLowerCase();
    const languageMap: Record<string, string> = {
      'js': 'javascript',
      'jsx': 'jsx',
      'ts': 'typescript',
      'tsx': 'tsx',
      'py': 'python',
      'java': 'java',
      'cpp': 'cpp',
      'c': 'c',
      'css': 'css',
      'scss': 'scss',
      'html': 'html',
      'json': 'json',
      'xml': 'xml',
      'yaml': 'yaml',
      'yml': 'yaml',
      'md': 'markdown',
      'sql': 'sql',
      'sh': 'bash',
      'php': 'php',
      'rb': 'ruby',
      'go': 'go',
      'rs': 'rust'
    };
    return languageMap[extension || ''] || 'text';
  };

  if (!diff || files.length === 0) {
    return (
      <div className={`flex flex-col items-center justify-center h-full bg-gray-50 ${className}`}>
        <FileText className="w-12 h-12 text-gray-400 mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">No Changes Available</h3>
        <p className="text-gray-600 text-center max-w-sm">
          {!diff ? 'Diff data is not available for this approval.' : 'No file changes found in the diff.'}
        </p>
        {prUrl && (
          <a
            href={prUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <ExternalLink className="w-4 h-4 mr-2" />
            View on GitHub
          </a>
        )}
      </div>
    );
  }

  return (
    <div className={`flex h-full ${className}`}>
      {/* File List Sidebar */}
      <div className="w-80 border-r bg-gray-50 flex flex-col">
        <div className="p-4 border-b bg-white">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Changed Files ({files.length})</h3>
            {prUrl && (
              <a
                href={prUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 transition-colors"
                title="View PR on GitHub"
              >
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>
          
          <div className="flex items-center space-x-2">
            <label className="text-xs font-medium text-gray-700">View:</label>
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value as 'unified' | 'split')}
              className="text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="unified">Unified</option>
              <option value="split">Split</option>
            </select>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div className="divide-y divide-gray-200">
            {files.map((file, index) => {
              const filePath = file.newPath || file.oldPath;
              const isExpanded = expandedFiles.has(filePath);
              const isSelected = selectedFile === filePath;

              return (
                <div key={index} className={`${isSelected ? 'bg-blue-100' : ''}`}>
                  <div
                    onClick={() => {
                      setSelectedFile(filePath);
                      if (!isExpanded) {
                        toggleFileExpansion(filePath);
                      }
                    }}
                    className={`p-3 cursor-pointer hover:bg-gray-100 border-l-4 ${getFileTypeColor(file.type)}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2 flex-1 min-w-0">
                        {getFileIcon(file.type)}
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {filePath.split('/').pop()}
                        </span>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleFileExpansion(filePath);
                        }}
                        className="p-1 hover:bg-gray-200 rounded"
                      >
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                    
                    {filePath !== filePath.split('/').pop() && (
                      <div className="text-xs text-gray-500 mt-1 truncate">
                        {filePath}
                      </div>
                    )}
                    
                    <div className="text-xs text-gray-600 mt-1">
                      {file.type === 'rename' ? 'Renamed' : 
                       file.type === 'add' ? 'Added' :
                       file.type === 'delete' ? 'Deleted' : 'Modified'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Diff Content */}
      <div className="flex-1 overflow-hidden">
        {selectedFile ? (
          <div className="h-full flex flex-col">
            {(() => {
              const file = files.find(f => (f.newPath || f.oldPath) === selectedFile);
              if (!file) return null;

              return (
                <div className="flex-1 overflow-auto bg-white">
                  <div className="p-4 border-b bg-gray-50">
                    <div className="flex items-center space-x-2">
                      {getFileIcon(file.type)}
                      <span className="font-medium text-gray-900">{selectedFile}</span>
                      <span className="text-xs text-gray-500">
                        ({file.type === 'rename' ? 'renamed' : 
                          file.type === 'add' ? 'added' :
                          file.type === 'delete' ? 'deleted' : 'modified'})
                      </span>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="mb-6">
                      <div className="bg-gray-100 px-3 py-1 text-xs font-mono text-gray-700 border rounded-t">
                        {file.newPath || file.oldPath}
                      </div>
                      <div className="border border-t-0 rounded-b overflow-hidden">
                        <SyntaxHighlighter
                          language={getLanguageFromPath(selectedFile)}
                          style={tomorrow}
                          customStyle={{
                            margin: 0,
                            padding: '1rem',
                            fontSize: '0.875rem',
                            lineHeight: '1.5'
                          }}
                          showLineNumbers
                          wrapLines
                        >
                          {file.content}
                        </SyntaxHighlighter>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            <div className="text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Select a File</h3>
              <p className="text-gray-600">
                Choose a file from the list to view its changes
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PRDiffViewer;
